import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Loader2, LogIn, UserPlus, ShieldCheck } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { EASE } from "@/lib/motion";

const DEPTS = ["RH", "Comptabilité & Paie", "Fiscalité"];

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ identifier: "", password: "", full_name: "", email: "", dept: "RH" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.identifier, form.password);
        toast.success("Connexion réussie. Bienvenue !");
      } else {
        await register({ full_name: form.full_name, email: form.email, password: form.password, dept: form.dept });
        toast.success("Votre demande d'accès a été envoyée. Le gérant doit valider votre compte.");
      }
      navigate("/espace");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain relative flex min-h-screen items-center justify-center overflow-hidden bg-korban-navy px-5 py-24">
      <div
        className="pointer-events-none absolute -left-32 -top-32 h-[420px] w-[420px] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(249,178,51,0.14) 0%, rgba(10,31,51,0) 70%)" }}
      />
      <div
        className="pointer-events-none absolute -bottom-40 -right-24 h-[420px] w-[420px] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(21,135,184,0.18) 0%, rgba(10,31,51,0) 70%)" }}
      />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="relative z-10 w-full max-w-md"
        data-testid="login-card"
      >
        <Link to="/" data-testid="back-to-site-link" className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-300 transition-colors hover:text-korban-gold">
          <ArrowLeft size={16} /> Retour au site
        </Link>

        <div className="rounded-3xl border border-white/10 bg-white/[0.05] p-8 shadow-[0_30px_80px_rgba(0,0,0,0.4)] backdrop-blur-xl sm:p-10">
          <div className="flex flex-col items-center text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white p-1.5 shadow-lg">
              <img src="/logo_korban_consulting.png" alt="KORBAN CONSULTING" className="h-full w-full object-contain" />
            </span>
            <h1 className="mt-5 font-display text-2xl font-bold text-white">Espace personnel</h1>
            <p className="mt-2 text-sm text-slate-400">Réservé au personnel de KORBAN CONSULTING</p>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-1 rounded-xl bg-korban-navy/70 p-1" data-testid="auth-tabs">
            {[
              ["login", "Connexion"],
              ["register", "Inscription"],
            ].map(([m, label]) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                data-testid={`auth-tab-${m}`}
                className={`rounded-lg py-2.5 text-sm font-bold transition-all ${mode === m ? "bg-korban-gold text-korban-navy" : "text-slate-300 hover:text-white"}`}
              >
                {label}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-6 grid gap-4" data-testid="auth-form">
            {mode === "login" ? (
              <>
                <div>
                  <label htmlFor="l-id" className="mb-1.5 block text-sm font-semibold text-slate-200">Email ou nom</label>
                  <input id="l-id" required value={form.identifier} onChange={set("identifier")}
                    placeholder="vous@korban.ci" data-testid="login-input-identifier" className="field" />
                </div>
                <div>
                  <label htmlFor="l-pw" className="mb-1.5 block text-sm font-semibold text-slate-200">Mot de passe</label>
                  <input id="l-pw" type="password" required value={form.password} onChange={set("password")}
                    placeholder="••••••••" data-testid="login-input-password" className="field" />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label htmlFor="r-name" className="mb-1.5 block text-sm font-semibold text-slate-200">Nom complet</label>
                  <input id="r-name" required minLength={2} value={form.full_name} onChange={set("full_name")}
                    placeholder="Votre nom et prénom" data-testid="register-input-name" className="field" />
                </div>
                <div>
                  <label htmlFor="r-email" className="mb-1.5 block text-sm font-semibold text-slate-200">Email</label>
                  <input id="r-email" type="email" required value={form.email} onChange={set("email")}
                    placeholder="vous@korban.ci" data-testid="register-input-email" className="field" />
                </div>
                <div>
                  <label htmlFor="r-pw" className="mb-1.5 block text-sm font-semibold text-slate-200">Mot de passe</label>
                  <input id="r-pw" type="password" required minLength={6} value={form.password} onChange={set("password")}
                    placeholder="6 caractères minimum" data-testid="register-input-password" className="field" />
                </div>
                <div>
                  <label htmlFor="r-dept" className="mb-1.5 block text-sm font-semibold text-slate-200">Département</label>
                  <select id="r-dept" value={form.dept} onChange={set("dept")} data-testid="register-select-dept" className="field">
                    {DEPTS.map((d) => (
                      <option key={d} value={d} className="bg-korban-navy">{d}</option>
                    ))}
                  </select>
                </div>
                <p className="flex items-start gap-2 rounded-lg bg-korban-blue/10 p-3 text-xs leading-relaxed text-slate-300">
                  <ShieldCheck size={15} className="mt-0.5 shrink-0 text-korban-blue" />
                  Votre compte sera <b>inactif</b> après l'inscription : le gérant l'activera et vous attribuera votre
                  département et votre rôle.
                </p>
              </>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid={mode === "login" ? "login-submit-button" : "register-submit-button"}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-lg bg-korban-gold px-6 py-3.5 font-bold text-korban-navy transition-all hover:-translate-y-0.5 hover:bg-korban-golddark disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? <Loader2 size={17} className="animate-spin" /> : mode === "login" ? <LogIn size={17} /> : <UserPlus size={17} />}
              {busy ? "Veuillez patienter…" : mode === "login" ? "Se connecter" : "Créer mon compte"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
